import * as React from "react";
import { Component, FormEvent, MouseEvent } from "react";

import { DebtorType } from "types";
import { ErrorExplanation } from "components";
import { getClasses } from "utils";
import { updateWageDebtorRequest } from "services/WageDebtor";
import { validDateOfBirth } from "utils/DateFormatters";
import withNavigation from "utils/withNavigation";

import AddressInfo from "./AddressInfo";
import ContactInfo from "./ContactInfo";
import PersonalInfo from "./PersonalInfo";
import SecurityInfo from "./SecurityInfo";
import { EditDebtorType } from "./types";

interface ErrorsType {
  passwordConfirmation?: string;
  server?: string[];
}

interface PropsType {
  debtor: DebtorType;
  navigate: Function;
  editPassword: boolean;
  setDebtor: Function;
  setNotice: Function;
}

interface StateType {
  debtor: EditDebtorType;
  errors: ErrorsType;
  loading: boolean;
}

const INITIAL_STATE = {
  errors: {},
  loading: false,
};

class Edit extends Component<PropsType, StateType> {
  static defaultProps = {
    editPassword: true,
  };

  state: StateType = {
    ...INITIAL_STATE,
    debtor: { ...this.props.debtor },
  };

  debtorFullName(): string {
    const { debtor } = this.state;

    return ["firstName", "middleName", "lastName"]
      .reduce((acc, part) => [...acc, debtor[part]], [])
      .join(" ");
  }

  goBack = (): void => {
    const { navigate } = this.props;
    navigate(-1);
  };

  isDateOfBirthValid = (): boolean =>
    validDateOfBirth(this.state.debtor.dateOfBirth);

  isPasswordValid = (): boolean => {
    const {
      debtor: { password, passwordConfirmation },
    } = this.state;

    return (
      (!password && !passwordConfirmation) || password === passwordConfirmation
    );
  };

  isValid = (): boolean => this.isDateOfBirthValid() && this.isPasswordValid();

  onCancel = (e: MouseEvent): void => {
    e.preventDefault();
    this.props.navigate(-1);
  };

  onSubmit = (e: FormEvent): void => {
    e.preventDefault();

    const { debtor } = this.state;
    const { setDebtor, setNotice } = this.props;

    this.validate();
    this.setState({ loading: true });

    updateWageDebtorRequest(debtor)
      .then(debtor => {
        setDebtor(debtor);
        setNotice("Profile Updated");
        this.props.navigate(-1);
      })
      .then(() => {
        this.setState({ loading: false });
      })
      .catch(errors => {
        this.setState({ errors: { server: errors }, loading: false });
      });
  };

  updateField = (e: {
    currentTarget: { name: string; value: string };
  }): void => {
    const {
      currentTarget: { name, value },
    } = e;
    const { debtor } = this.state;

    this.setState({ debtor: { ...debtor, [name]: value } });
  };

  validate = (): void => {
    const {
      debtor: { password, passwordConfirmation },
    } = this.state;

    const errors: ErrorsType = { ...INITIAL_STATE.errors };

    if (password && passwordConfirmation && password !== passwordConfirmation) {
      errors.passwordConfirmation =
        "Password Confirmation doesn't match Password";
    }

    this.setState({ errors });
  };

  visibleSections(): string[] {
    const sections = ["PersonalInfo", "ContactInfo", "Address"];

    if (this.props.editPassword) {
      sections.push("SecurityInfo");
    }

    return sections;
  }

  render(): JSX.Element {
    const { editPassword } = this.props;
    const { debtor, errors, loading } = this.state;

    const disabled = !this.isValid() || loading;

    return (
      <div id="wage-debtor-edit">
        <h2>
          <i className="fas fa-edit" />
          <span>Edit Debtor &bull; {this.debtorFullName()}</span>
        </h2>

        <ErrorExplanation errors={errors.server} title="Profile Errors" />

        <form onSubmit={this.onSubmit}>
          <article
            className="fieldsets"
            data-visible-section-count={this.visibleSections().length}
          >
            <PersonalInfo
              isDateOfBirthValid={this.isDateOfBirthValid}
              onFieldBlur={this.validate}
              onFieldChange={this.updateField}
              {...{ debtor }}
            />

            <ContactInfo
              onFieldBlur={this.validate}
              onFieldChange={this.updateField}
              parent={this}
              {...{ debtor }}
            />

            <AddressInfo
              onFieldBlur={this.validate}
              onFieldChange={this.updateField}
              {...{ debtor }}
            />

            <SecurityInfo
              hidden={!editPassword}
              onFieldBlur={this.validate}
              onFieldChange={this.updateField}
              {...{ debtor, errors }}
            />
          </article>

          <div className="form--actions">
            <button
              className="button--green"
              disabled={loading || disabled}
              type="submit"
            >
              <i
                className={getClasses("fas", {
                  "fa-spin fa-sync-alt": loading,
                  "fa-save": !loading,
                })}
              />
              <span>{loading ? "Saving" : "Save"}</span>
            </button>

            <button className="button--gray" onClick={this.onCancel}>
              <i className="fas fa-ban" />
              <span>Cancel</span>
            </button>
          </div>
        </form>
      </div>
    );
  }
}

export default withNavigation(Edit);